import * as React from "react";
import { Flex, Form, Input, InputNumber, Select, Switch } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { UserProfile } from "../../common/services/types";
import { Utils } from "../../common/misc/Utils";

const FormItem = Form.Item;
type FormItemRules = React.ComponentProps<typeof FormItem>["rules"];

export const periodOptions = ["day(s)", "week(s)", "month(s)", "year(s)"];
export const dialogPeriodOptions = [
    { label: "day(s)", value: "D" },
    { label: "week(s)", value: "W" },
    { label: "month(s)", value: "M" },
    { label: "year(s)", value: "Y" },
];

export const generateTaskTypeCode = (name?: string) =>
    (name ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const getUserFullName = (user: UserProfile) =>
    `${user.firstName} ${user.lastName}`;

export const getDefaultAssigneeOptions = (
    users: UserProfile[],
    showInactiveStatus = false,
) =>
    users
        .slice()
        .sort((firstUser, secondUser) =>
            getUserFullName(firstUser).localeCompare(
                getUserFullName(secondUser),
                undefined,
                { sensitivity: "base" },
            ),
        )
        .map((user) => ({
            value: user.userId,
            label: `${getUserFullName(user)}${
                showInactiveStatus && user.isDeleted ? " (Inactive)" : ""
            }`,
            customFilterValue: getUserFullName(user),
        }));

type Props = {
    form: FormInstance;
    assigneeFieldName: "asignee" | "assignee";
    assigneeInitialValue?: string | null;
    assigneeUsers: UserProfile[];
    showInactiveAssigneeStatus?: boolean;
    nameRules: FormItemRules;
    codeRules: FormItemRules;
    shouldAutoGenerateCode?: boolean;
};

const TaskTypeDialogForm: React.FC<Props> = ({
    form,
    assigneeFieldName,
    assigneeInitialValue,
    assigneeUsers,
    showInactiveAssigneeStatus = false,
    nameRules,
    codeRules,
    shouldAutoGenerateCode = false,
}) => {
    const count = Form.useWatch("count", form);
    const period = Form.useWatch("period", form);

    const hasDuePeriod = Boolean(count && period);
    const maxReminderDays =
        hasDuePeriod && period ? Utils.getDuePeriodDays(period) * count : 0;
    const canConfigureReminder = hasDuePeriod;

    React.useEffect(() => {
        if (canConfigureReminder) {
            return;
        }

        form.setFieldValue("reminderDays", null);
    }, [canConfigureReminder, form]);

    return (
        <>
            <Flex vertical className="task-type-create-section">
                <FormItem
                    label={<span className="dialog-field-label">Name</span>}
                    name="name"
                    rules={nameRules}
                >
                    <Input
                        data-id="input-Name"
                        placeholder="Enter task type name"
                        onChange={(event) => {
                            if (!shouldAutoGenerateCode) {
                                return;
                            }

                            form.setFieldsValue({
                                code: generateTaskTypeCode(event.target.value),
                            });
                        }}
                    />
                </FormItem>
                <FormItem
                    label={<span className="dialog-field-label">Code</span>}
                    name="code"
                    rules={codeRules}
                >
                    <Input
                        data-id="input-code"
                        placeholder="TASK_TYPE_CODE"
                        disabled
                        suffix={<LockOutlined />}
                    />
                </FormItem>
            </Flex>

            <Flex vertical className="task-type-create-section">
                <div className="modal-section-title">
                    <span>Defaults and behaviour</span>
                </div>
                <FormItem
                    label={
                        <span className="dialog-field-label">
                            Default assignee
                        </span>
                    }
                    name={assigneeFieldName}
                    initialValue={assigneeInitialValue}
                    className="task-type-create-assignee"
                >
                    <Select
                        allowClear
                        showSearch
                        placeholder="Select user"
                        filterOption={Utils.filterOption}
                        options={getDefaultAssigneeOptions(
                            assigneeUsers,
                            showInactiveAssigneeStatus,
                        )}
                    />
                </FormItem>
                <div className="task-type-create-defaults-row">
                    <FormItem
                        label={
                            <span className="dialog-field-label">
                                Due period
                            </span>
                        }
                        className="task-type-create-due-period"
                    >
                        <FormItem name="count" noStyle>
                            <InputNumber
                                data-id="input-count"
                                min={1}
                                placeholder="e.g. 5"
                            />
                        </FormItem>
                        <FormItem name="period" noStyle>
                            <Select
                                data-id="input-period"
                                placeholder="day(s)"
                                options={dialogPeriodOptions}
                            />
                        </FormItem>
                    </FormItem>
                    {hasDuePeriod && (
                        <FormItem
                            name="reminderDays"
                            label={
                                <span className="dialog-field-label">
                                    Reminder before due date
                                </span>
                            }
                            className="task-type-create-reminder"
                        >
                            <InputNumber
                                data-id="input-reminder-days"
                                min={1}
                                max={maxReminderDays}
                                disabled={!canConfigureReminder}
                                controls={canConfigureReminder}
                                addonAfter="day(s)"
                            />
                        </FormItem>
                    )}
                </div>
                <div className="create-project-form__metadata-row">
                    <div className="setting-field-text">
                        <div className="setting-field-title">
                            Allow manual status changes
                        </div>
                    </div>
                    <FormItem
                        name="canChangeStatusManually"
                        valuePropName="checked"
                        className="create-project-form__metadata-switch"
                    >
                        <Switch data-id="input-can-change-status-manually" />
                    </FormItem>
                </div>
            </Flex>
        </>
    );
};

export default TaskTypeDialogForm;
